import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
  PracticeLevelResponse,
  PracticeLevelRequest,
  SkillDocumentResponse,
} from '../../../models/skill-learning.model';

const MIN_DESCRIPTION_LENGTH = 50;

type PracticeLevelInput = PracticeLevelResponse | null;
type SkillDocumentInput = SkillDocumentResponse | null;

export interface PracticeLevelSavePayload {
  request: PracticeLevelRequest;
  documentFile: File | null;
}

@Component({
  selector: 'app-skill-practice-level-editor',
  templateUrl: './practice-level-editor.component.html',
  styleUrls: ['./practice-level-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillPracticeLevelEditorComponent implements OnInit, OnChanges {

  @Input() practiceLevel: PracticeLevelInput = null;
  @Input() levelNumber: number = 2;
  @Input() documentFileId: string | null = null;
  @Input() document: SkillDocumentInput = null;
  @Input() isSubmitting: boolean = false;

  @Output() levelSaved = new EventEmitter<PracticeLevelSavePayload>();
  @Output() documentDownloadRequested = new EventEmitter<void>();

  form: FormGroup;
  pendingDocument: File | null = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.practiceLevel && changes.practiceLevel.currentValue) {
      this.form = this.buildForm();
    }
  }

  get isFormInvalid(): boolean {
    return this.form.invalid || !this.hasRequiredDocument;
  }

  get hasRequiredDocument(): boolean {
    return this.hasPendingDocument || this.hasUploadedDocument;
  }

  get hasPendingDocument(): boolean {
    return !!this.pendingDocument;
  }

  get hasUploadedDocument(): boolean {
    if (this.hasDocumentMetadata()) {
      return true;
    }
    return this.hasDocumentIdentifier(this.documentFileId);
  }

  get descriptionLength(): number {
    const value: string = this.form.get('description').value || '';
    return value.length;
  }

  get minDescriptionLength(): number {
    return MIN_DESCRIPTION_LENGTH;
  }

  onDocumentSelected(file: File | null): void {
    this.pendingDocument = file;
  }

  onDocumentDownload(): void {
    this.documentDownloadRequested.emit();
  }

  onSave(): void {
    if (this.isFormInvalid) {
      return;
    }
    const request: PracticeLevelRequest = {
      description: this.form.get('description').value,
    };
    this.levelSaved.emit({
      request,
      documentFile: this.pendingDocument,
    });
    this.pendingDocument = null;
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      description: [
        this.practiceLevel ? this.practiceLevel.description : '',
        [Validators.required, Validators.minLength(MIN_DESCRIPTION_LENGTH)],
      ],
    });
  }

  private hasDocumentMetadata(): boolean {
    if (!this.document) {
      return false;
    }
    return this.hasDocumentIdentifier(this.document.fileId);
  }

  private hasDocumentIdentifier(fileId: string | null): boolean {
    if (!fileId) {
      return false;
    }
    return fileId.trim().length > 0;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/practice-level-editor/practice-level-editor.component.ts